import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { adminApi } from '../utils/api'
import { getDisplaySundayDateStr } from '../utils/sundayDisplay'
import { t } from '../utils/translations'
import { openFileDownload, resolveFileRef } from '../utils/fileUrl'
import { copyRichHtmlForWordPress, copyRichHtmlWithLazyLoad, buildQuickEntryCopy, buildChurchCopyFromSections } from '../utils/copyToWordPress'

const CHURCHES = ['KTC', 'SSC', 'WIC', 'STC']
const DEFAULT_DATE_COUNT = 3
const CARD_TITLE_MAX = 15
const CARD_SPEAKER_MAX = 10
const DEFAULT_TIME_BY_TYPE = {
  Sermon: '10:45',
  SundaySchool: '09:30'
}

function truncateCardText(text, maxLen) {
  if (!text) return ''
  const chars = Array.from(String(text))
  if (chars.length <= maxLen) return String(text)
  return `${chars.slice(0, maxLen).join('')}…`
}

export default function AdminPage({ lang = 'zh', weeksToShow = '3' }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [overview, setOverview] = useState(null)
  const [selectedDate, setSelectedDate] = useState(null)
  const [dateEntries, setDateEntries] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [copySuccess, setCopySuccess] = useState(false)
  const [copyModalDate, setCopyModalDate] = useState(null)
  const [copyModalText, setCopyModalText] = useState('')
  const [copyModalHtml, setCopyModalHtml] = useState('')
  const [copyModalLoading, setCopyModalLoading] = useState(false)
  const [openAttachments, setOpenAttachments] = useState(null)
  const [copyCache, setCopyCache] = useState({})
  const copyCacheRef = useRef({})
  const [entryCopySuccess, setEntryCopySuccess] = useState(null)
  const [churchCopySuccess, setChurchCopySuccess] = useState(null)
  /** { [date]: { [church]: { ready, youtube_url, ... } } } */
  const [youtubeByDate, setYoutubeByDate] = useState({})
  const [refreshingChurch, setRefreshingChurch] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!openAttachments) return undefined
    const close = () => setOpenAttachments(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [openAttachments])

  const loadData = async () => {
    try {
      setLoading(true)
      const allResponse = await adminApi.getOverview('all')
      const allData = allResponse.data.data

      setOverview(allData)

    } catch (err) {
      setError('Failed to load data')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const invalidateCopyCache = (date) => {
    if (!date) return
    delete copyCacheRef.current[date]
    setCopyCache(prev => {
      if (!(date in prev)) return prev
      const next = { ...prev }
      delete next[date]
      return next
    })
  }

  const applyYoutubeStatus = (date, churches) => {
    setYoutubeByDate(prev => ({
      ...prev,
      [date]: Object.fromEntries(
        CHURCHES.map(c => {
          const row = churches?.[c] || {}
          return [c, {
            ready: Boolean(row.ready ?? row.youtube_url),
            // Only true after cron/refresh/yellow tried — not just "no link in DB yet"
            attempted: Boolean(row.attempted ?? row.fetched_at),
            youtube_url: row.youtube_url || null,
            fetched_at: row.fetched_at || null
          }]
        })
      )
    }))
  }

  const loadYoutubeStatus = async (date) => {
    if (!date) return
    try {
      const response = await adminApi.getYoutubeStatus(date)
      applyYoutubeStatus(date, response.data?.data?.churches)
    } catch (err) {
      console.error('YouTube status failed:', err)
    }
  }

  const isEntryReady = (entry) => entry?.status === 'submitted'

  const isSundaySchoolReady = (entry) => {
    if (!isEntryReady(entry)) return false
    // 「暫停一次」 submitted SundaySchool counts as ready
    return true
  }

  const getChurchCopyState = (dateGroup, church) => {
    const churchData = dateGroup.churches?.[church]
    const sermonEntry = getEntryByType(churchData?.entries, 'Sermon')
    const ssEntry = getEntryByType(churchData?.entries, 'SundaySchool')
    const ytStatus = youtubeByDate[dateGroup.date]
    const ytStatusLoaded = Boolean(ytStatus)
    const ytReady = Boolean(ytStatus?.[church]?.ready)
    const ytAttempted = Boolean(ytStatus?.[church]?.attempted)
    const sermonReady = isEntryReady(sermonEntry)
    const ssReady = isSundaySchoolReady(ssEntry)
    const copyReady = ytReady && sermonReady && ssReady
    return {
      ytReady,
      ytAttempted,
      ytStatusLoaded,
      sermonReady,
      ssReady,
      copyReady,
      sermonEntry,
      ssEntry
    }
  }

  const formatDateStr = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  const loadDateEntries = async (date) => {
    try {
      const response = await adminApi.getEntriesByDate(date)
      setDateEntries(response.data.data)
      setSelectedDate(date)
    } catch (err) {
      setError('Failed to load entries')
      console.error(err)
    }
  }

  const formatDateDisplay = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return dateStr
    const year = dateStr.substring(0, 4)
    const month = dateStr.substring(4, 6)
    const day = dateStr.substring(6, 8)
    return `${year}-${month}-${day}`
  }

  const getDayInfo = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return { text: '', isSunday: false, isSaturday: false }
    const year = parseInt(dateStr.substring(0, 4))
    const month = parseInt(dateStr.substring(4, 6)) - 1
    const day = parseInt(dateStr.substring(6, 8))
    const date = new Date(year, month, day)
    const dayNum = date.getDay()
    const daysZh = ['日', '一', '二', '三', '四', '五', '六']
    const daysEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    return {
      text: lang === 'zh' ? daysZh[dayNum] : daysEn[dayNum],
      isSunday: dayNum === 0,
      isSaturday: dayNum === 6
    }
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
        return <span className="badge badge-success">{t('submitted', lang)}</span>
      case 'saved':
        return <span className="badge badge-warning">{t('saved', lang)}</span>
      default:
        return <span className="badge badge-danger">{t('noSubmission', lang)}</span>
    }
  }

  const getStatusBadgeOrange = (status) => {
    switch (status) {
      case 'submitted':
        return <span className="badge badge-success">{t('submitted', lang)}</span>
      case 'saved':
        return <span className="badge badge-warning">{t('saved', lang)}</span>
      default:
        return <span className="badge badge-orange">{t('noSubmission', lang)}</span>
    }
  }

  const canCreateForChurch = (church) => {
    if (!user || user.role === 'viewer') return false
    if (user.role === 'admin' || user.role === 'sysadmin') return true
    const churches = user.churches || (user.church ? [user.church] : [])
    return churches.includes(church)
  }

  const openNewEntryFromMissing = (e, { church, type, eventDate }) => {
    e.stopPropagation()
    if (!canCreateForChurch(church)) return

    const params = new URLSearchParams({
      church,
      type,
      date: eventDate,
      time: DEFAULT_TIME_BY_TYPE[type] || '',
      from: 'admin'
    })
    navigate(`/event?${params.toString()}`)
  }

  const getEntryByType = (entries, type) => {
    if (!entries || entries.length === 0) return null
    return entries.find(e => e.type === type || (type === 'SundaySchool' && e.type === 'SundaySchool'))
  }

  const renderEntryRow = (entry, typeLabelKey, eventDate, church, entryType) => {
    const fileList = entry?.file_urls || []
    const fileCount = fileList.length
    const files = fileList.map(f => resolveFileRef(f, church)).filter(Boolean)
    const menuKey = entry?.entry_id || null
    const isMenuOpen = menuKey && openAttachments === menuKey

    const handleAttachmentClick = async (e) => {
      e.stopPropagation()
      if (!menuKey || fileCount === 0) return
      if (fileCount === 1) {
        try {
          await openFileDownload(files[0], church)
        } catch (err) {
          console.error('Download failed:', err)
          const is404 = err.response?.status === 404 || err.code === 'NOT_FOUND'
          setError(
            is404
              ? (lang === 'zh'
                ? '附件不存在於伺服器，請至活動面板重新上傳'
                : 'Attachment not found on server, please re-upload via Activity Panel')
              : (lang === 'zh' ? '下載附件失敗' : 'Failed to download attachment')
          )
        }
        return
      }
      setOpenAttachments(isMenuOpen ? null : menuKey)
    }

    const handleMenuDownload = async (e, file) => {
      e.preventDefault()
      e.stopPropagation()
      try {
        await openFileDownload(file, church)
      } catch (err) {
        console.error('Download failed:', err)
        const is404 = err.response?.status === 404 || err.code === 'NOT_FOUND'
        setError(
          is404
            ? (lang === 'zh'
              ? '附件不存在於伺服器，請至活動面板重新上傳'
              : 'Attachment not found on server, please re-upload via Activity Panel')
            : (lang === 'zh' ? '下載附件失敗' : 'Failed to download attachment')
        )
      }
    }

    const handleCopyEntry = (e) => {
      e.stopPropagation()
      if (!entry?.entry_id || !eventDate) return

      setError('')
      const { html, text } = buildQuickEntryCopy(entry, eventDate)
      copyRichHtmlForWordPress(html, text)
        .then(() => {
          setEntryCopySuccess(entry.entry_id)
          setTimeout(() => setEntryCopySuccess(null), 2000)
        })
        .catch(err => {
          console.error('Entry copy failed:', err)
          setError(lang === 'zh' ? '複製失敗，請允許剪貼簿權限' : 'Copy failed — allow clipboard permission')
        })
    }

    return (
      <div className="entry-row-inline">
        <div className="entry-row-label">
          <span className="entry-type-label">{t(typeLabelKey, lang)}</span>
          {/* Always reserve 2 meta lines so rows align across church cards */}
          <span className={`entry-meta entry-meta-title${!entry?.title ? ' is-empty' : ''}`} title={entry?.title || ''}>
            {entry?.title ? truncateCardText(entry.title, CARD_TITLE_MAX) : '\u00a0'}
          </span>
          <span className={`entry-meta entry-meta-speaker${!entry?.speaker ? ' is-empty' : ''}`} title={entry?.speaker || ''}>
            {entry?.speaker ? truncateCardText(entry.speaker, CARD_SPEAKER_MAX) : '\u00a0'}
          </span>
        </div>
        <div className="entry-row-status">
          {fileCount > 0 && (
            <div className="file-indicator-wrap">
              <button
                type="button"
                className="file-indicator"
                title={
                  lang === 'zh'
                    ? `點擊下載附件 (${fileCount})`
                    : `Click to download (${fileCount})`
                }
                onClick={handleAttachmentClick}
              >
                📎{fileCount > 1 ? fileCount : ''}
              </button>
              {isMenuOpen && fileCount > 1 && (
                <div
                  className="attachment-menu"
                  role="menu"
                  onClick={e => e.stopPropagation()}
                >
                  {files.map((file, i) => (
                    <button
                      key={i}
                      type="button"
                      className="attachment-menu-item"
                      role="menuitem"
                      onClick={e => handleMenuDownload(e, file)}
                    >
                      {file.filename || file.key || (lang === 'zh' ? '檔案' : 'File')}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {entry && (
            <button
              type="button"
              className="btn-copy-entry"
              title={lang === 'zh' ? '複製此段（含格式）貼到 WordPress' : 'Copy section for WordPress'}
              onClick={handleCopyEntry}
            >
              {entryCopySuccess === entry.entry_id
                ? (lang === 'zh' ? '已複製' : 'Copied')
                : '📋'}
            </button>
          )}
          {entry ? (
            getStatusBadge(entry.status)
          ) : canCreateForChurch(church) ? (
            <button
              type="button"
              className="badge badge-orange badge-orange-clickable"
              title={lang === 'zh' ? '點擊新增條目' : 'Click to add entry'}
              onClick={(e) => openNewEntryFromMissing(e, {
                church,
                type: entryType,
                eventDate
              })}
            >
              {t('noSubmission', lang)}
            </button>
          ) : (
            getStatusBadgeOrange('missing')
          )}
        </div>
      </div>
    )
  }

  const isSundayDate = (dateStr) => {
    if (!dateStr || dateStr.length !== 8) return false
    const year = parseInt(dateStr.substring(0, 4), 10)
    const month = parseInt(dateStr.substring(4, 6), 10) - 1
    const day = parseInt(dateStr.substring(6, 8), 10)
    return new Date(year, month, day).getDay() === 0
  }

  const displayDates = useMemo(() => {
    if (!overview?.dates) return []

    // Sermon/SundaySchool overview: Sundays only (exclude mid-week event dates)
    const sundayDates = overview.dates
      .filter(d => d.is_sunday === true || isSundayDate(d.date))
      .sort((a, b) => b.date.localeCompare(a.date))

    if (weeksToShow === 'all') {
      return sundayDates
    }

    const count = Math.max(1, parseInt(weeksToShow, 10) || DEFAULT_DATE_COUNT)
    const maxDate = getDisplaySundayDateStr()
    return sundayDates
      .filter(d => d.date <= maxDate)
      .slice(0, count)
  }, [overview, weeksToShow])

  const fetchCopyData = async (date) => {
    if (copyCacheRef.current[date]) return copyCacheRef.current[date]
    const response = await adminApi.getCopyText(date)
    const data = response.data.data || {}
    copyCacheRef.current[date] = data
    setCopyCache(prev => ({ ...prev, [date]: data }))
    return data
  }

  const ensureYoutubeForChurch = async (date, church) => {
    const response = await adminApi.refreshYoutube(date, church)
    const churches = response.data?.data?.churches
    applyYoutubeStatus(date, churches)
    invalidateCopyCache(date)
    return Boolean(churches?.[church]?.ready ?? churches?.[church]?.youtube_url)
  }

  const handleRefreshYoutube = async (e, date, church) => {
    e.stopPropagation()
    setError('')
    const key = `${date}-${church}`
    setRefreshingChurch(key)
    try {
      await ensureYoutubeForChurch(date, church)
    } catch (err) {
      console.error('YouTube refresh failed:', err)
      setError(lang === 'zh' ? '重新抓取 YouTube 失敗' : 'YouTube refresh failed')
    } finally {
      setRefreshingChurch(null)
    }
  }

  const handleCopyChurchText = (e, date, church, { ytReady = false, ytStatusLoaded = false } = {}) => {
    e.stopPropagation()
    setError('')
    const churchName = t(church, lang)
    const key = `${date}-${church}`

    copyRichHtmlWithLazyLoad(async () => {
      // Yellow + no YT yet: try live API (also records failed attempt → warning)
      if (ytStatusLoaded && !ytReady) {
        setRefreshingChurch(key)
        try {
          await ensureYoutubeForChurch(date, church)
        } finally {
          setRefreshingChurch(null)
        }
      }
      const data = await fetchCopyData(date)
      const churchSections = (data.sections || []).filter(s => s.church === church)
      // Yellow: omit missing fields — empty church header is still valid
      return buildChurchCopyFromSections(churchSections, churchName)
    })
      .then(() => {
        setChurchCopySuccess(key)
        setTimeout(() => setChurchCopySuccess(null), 2000)
      })
      .catch(err => {
        console.error('Church copy failed:', err)
        setError(lang === 'zh' ? '複製失敗，請允許剪貼簿權限' : 'Copy failed — allow clipboard permission')
      })
  }

  const tableDates = displayDates

  useEffect(() => {
    if (!displayDates.length) return
    displayDates.forEach(d => {
      if (!youtubeByDate[d.date]) {
        loadYoutubeStatus(d.date)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once per newly shown date
  }, [displayDates])

  const handleCopyText = async (date) => {
    try {
      setError('')
      setCopyModalLoading(true)
      setCopyModalDate(date)
      setCopyModalText('')
      setCopyModalHtml('')
      const data = await fetchCopyData(date)
      setCopyModalText(data.text || '')
      setCopyModalHtml(data.html || '')
    } catch (err) {
      console.error('Copy failed:', err)
      setError(lang === 'zh' ? '載入複製文字失敗' : 'Failed to load copy text')
      setCopyModalDate(null)
    } finally {
      setCopyModalLoading(false)
    }
  }

  const handleCopyToClipboard = async () => {
    try {
      const plain = copyModalText || ''
      const html = copyModalHtml || plain
      await copyRichHtmlForWordPress(html, plain)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Clipboard failed:', err)
      setError(lang === 'zh' ? '複製失敗' : 'Copy failed')
    }
  }

  const closeCopyModal = () => {
    setCopyModalDate(null)
    setCopyModalText('')
    setCopyModalHtml('')
  }

  if (loading) {
    return <div className="container">{t('loading', lang)}</div>
  }

  if (error) {
    return <div className="message message-error">{error}</div>
  }


  const renderChurchCards = (dateGroup) => (
    <div className="church-cards">
      {CHURCHES.map(church => {
        const { ytReady, ytAttempted, ytStatusLoaded, copyReady, sermonEntry, ssEntry } =
          getChurchCopyState(dateGroup, church)
        const actionKey = `${dateGroup.date}-${church}`
        const isRefreshing = refreshingChurch === actionKey
        const copyLabel = churchCopySuccess === actionKey
          ? (lang === 'zh' ? '已複製' : 'Copied')
          : isRefreshing
            ? t('refreshingYoutube', lang)
            : (ytReady ? t('copyText', lang) : t('copyTextSlow', lang))
        return (
          <div
            key={church}
            className="church-card"
            onClick={() => loadDateEntries(dateGroup.date)}
          >
            <div className="church-card-header">
              <span className="church-name">{t(church, lang)}</span>
              <div className="church-card-actions">
                {ytReady && (
                  <button
                    type="button"
                    className="btn-refresh-youtube"
                    title={t('refreshYoutube', lang)}
                    disabled={isRefreshing}
                    onClick={(e) => handleRefreshYoutube(e, dateGroup.date, church)}
                    aria-label={t('refreshYoutube', lang)}
                  >
                    ↻
                  </button>
                )}
                <button
                  type="button"
                  className={`btn-copy-church ${copyReady ? 'btn-copy-ready' : 'btn-copy-pending'}`}
                  title={lang === 'zh' ? '複製此堂文字' : 'Copy this church'}
                  disabled={isRefreshing}
                  onClick={(e) => handleCopyChurchText(e, dateGroup.date, church, { ytReady, ytStatusLoaded })}
                >
                  {copyLabel}
                </button>
              </div>
            </div>
            {ytAttempted && !ytReady && (
              <p className="church-yt-warning">{t('youtubeNotReady', lang)}</p>
            )}
            <div className="church-card-body">
              {renderEntryRow(ssEntry, 'sundaySchool', dateGroup.date, church, 'SundaySchool')}
              {renderEntryRow(sermonEntry, 'sermon', dateGroup.date, church, 'Sermon')}
            </div>
          </div>
        )
      })}
    </div>
  )

  return (
    <div className="admin-page">
      {displayDates.length === 0 ? (
        <div className="no-events">
          {lang === 'zh' ? '沒有紀錄' : 'No records'}
        </div>
      ) : (
        displayDates.map(dateGroup => (
          <div key={dateGroup.date} className="sunday-dashboard">
            <div className="dashboard-header">
              <h2>{formatDateDisplay(dateGroup.date)}</h2>
              <button
                type="button"
                className="btn-copy-text"
                onClick={(e) => {
                  e.stopPropagation()
                  handleCopyText(dateGroup.date)
                }}
              >
                {t('copyTextAll', lang)}
              </button>
            </div>
            {renderChurchCards(dateGroup)}
          </div>
        ))
      )}

      {/* All Events List */}
      <div className="all-events-section">
        <h3>{t('allEvents', lang)}</h3>
        <div className="events-table">
          <div className="events-table-header">
            <div className="col-date">{t('eventDate', lang)}</div>
            <div className="col-time">{lang === 'zh' ? '時間' : 'Time'}</div>
            <div className="col-church">{t('church', lang)}</div>
            <div className="col-title">{t('title', lang)}</div>
            <div className="col-type">{t('type', lang)}</div>
            <div className="col-speaker">{t('speaker', lang)}</div>
            <div className="col-status">{t('status', lang)}</div>
            <div className="col-files">{lang === 'zh' ? '附件' : 'Files'}</div>
          </div>
          <div className="events-table-body">
            {tableDates.length === 0 ? (
              <div className="no-events">
                {lang === 'zh' ? '沒有活動' : 'No events'}
              </div>
            ) : (
              tableDates.map(dateGroup => {
                const isPast = dateGroup.date < formatDateStr(new Date())
                return (
                  dateGroup.churches && Object.entries(dateGroup.churches).map(([church, data]) => (
                    data.entries
                      ?.filter(entry => entry.type === 'Sermon' || entry.type === 'SundaySchool')
                      .map((entry, idx) => {
                      const dayInfo = getDayInfo(dateGroup.date)
                      return (
                        <div
                          key={`${dateGroup.date}-${church}-${idx}`}
                          className={`events-table-row ${isPast ? 'past-event' : ''}`}
                          onClick={() => loadDateEntries(dateGroup.date)}
                        >
                          <div className="col-date">
                            <span className={isPast ? 'text-muted' : ''}>{formatDateDisplay(dateGroup.date)}</span>
                            <span className={`day-of-week ${dayInfo.isSunday ? 'sunday' : dayInfo.isSaturday ? 'saturday' : ''}`}>
                              {dayInfo.text}
                            </span>
                          </div>
                          <div className={`col-time ${isPast ? 'text-muted' : ''}`}>{entry.time || '-'}</div>
                          <div className={`col-church ${isPast ? 'text-muted' : ''}`}>{t(church, lang)}</div>
                          <div className={`col-title ${isPast ? 'text-muted' : ''}`}>{entry.title}</div>
                          <div className={`col-type ${isPast ? 'text-muted' : ''}`}>
                            {t(entry.type === 'SundaySchool' ? 'sundaySchool' : entry.type.toLowerCase(), lang)}
                          </div>
                          <div className={`col-speaker ${isPast ? 'text-muted' : ''}`}>{entry.speaker || '-'}</div>
                          <div className="col-status">{getStatusBadge(entry.status)}</div>
                          <div className={`col-files ${isPast ? 'text-muted' : ''}`}>
                            {entry.file_urls?.length > 0 ? `📎 ${entry.file_urls.length}` : '-'}
                          </div>
                        </div>
                      )
                    })
                  ))
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* Copy Text Modal */}
      {copyModalDate && (
        <div className="modal-overlay" onClick={closeCopyModal}>
          <div className="modal copy-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {formatDateDisplay(copyModalDate)}
                <span className="modal-subtitle">{t('copyText', lang)}</span>
              </h3>
              <button type="button" className="btn-close" onClick={closeCopyModal}>×</button>
            </div>
            <div className="modal-body">
              {copyModalLoading ? (
                <p className="copy-loading">{t('loading', lang)}</p>
              ) : (
                <>
                  <div
                    className="copy-preview"
                    dangerouslySetInnerHTML={{ __html: copyModalHtml }}
                  />
                  <p className="copy-hint">
                    {lang === 'zh'
                      ? '貼到 WordPress：先按「複製到 WordPress」，再在編輯器「可视化」分頁 Cmd+V。若格式消失，改在「文本」分頁貼上。各堂卡片 📋 可只複製該段（講道/主日學）。'
                      : 'Paste in WordPress Visual tab after copying. Use Text tab if formatting is lost. Use 📋 on each row to copy one section.'}
                  </p>
                  {copySuccess && (
                    <p className="copy-success-msg">{lang === 'zh' ? '已複製到剪貼簿' : 'Copied to clipboard'}</p>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={closeCopyModal}>
                {t('cancel', lang)}
              </button>
              <button
                type="button"
                className="btn-copy-text"
                onClick={handleCopyToClipboard}
                disabled={copyModalLoading || (!copyModalText && !copyModalHtml)}
              >
                {lang === 'zh' ? '複製到 WordPress' : 'Copy for WordPress'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Date Detail Modal */}
      {selectedDate && dateEntries && (
        <div className="modal-overlay" onClick={() => setSelectedDate(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{formatDateDisplay(selectedDate)}</h3>
              <button className="btn-close" onClick={() => setSelectedDate(null)}>×</button>
            </div>
            <div className="modal-body">
              {CHURCHES.map(church => (
                <div key={church} className="church-detail">
                  <h4>{t(church, lang)}</h4>
                  {dateEntries.churches[church]?.length > 0 ? (
                    dateEntries.churches[church].map((entry, idx) => (
                      <div key={idx} className="entry-detail">
                        <div className="entry-row">
                          <span className="label">{t('type', lang)}:</span>
                          <span>{t(entry.type === 'SundaySchool' ? 'sundaySchool' : entry.type.toLowerCase(), lang)}</span>
                        </div>
                        <div className="entry-row">
                          <span className="label">{t('title', lang)}:</span>
                          <span>{entry.title}</span>
                        </div>
                        {entry.time && (
                          <div className="entry-row">
                            <span className="label">{t('time', lang)}:</span>
                            <span>{entry.time}</span>
                          </div>
                        )}
                        <div className="entry-row">
                          <span className="label">{t('speaker', lang)}:</span>
                          <span>{entry.speaker || '-'}</span>
                        </div>
                        <div className="entry-row">
                          <span className="label">{t('scripture', lang)}:</span>
                          <span>{entry.scripture || '-'}</span>
                        </div>
                        {entry.youtube_url && (
                          <div className="entry-row">
                            <span className="label">YouTube:</span>
                            <a href={entry.youtube_url} target="_blank" rel="noopener noreferrer">
                              {entry.youtube_url}
                            </a>
                          </div>
                        )}
                        {entry.zoom_url && (
                          <div className="entry-row">
                            <span className="label">Zoom:</span>
                            <a href={entry.zoom_url} target="_blank" rel="noopener noreferrer">
                              {entry.zoom_url}
                            </a>
                          </div>
                        )}
                        {entry.description && (
                          <div className="entry-row">
                            <span className="label">{t('description', lang)}:</span>
                            <span>{entry.description}</span>
                          </div>
                        )}
                        <div className="entry-row">
                          <span className="label">{t('status', lang)}:</span>
                          {getStatusBadge(entry.status)}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-entry-detail">
                      {lang === 'zh' ? '無條目' : 'No entries'}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Copy Success Toast */}
      {copySuccess && (
        <div className="toast">
          {lang === 'zh' ? '已複製，可貼到 WordPress' : 'Copied — paste into WordPress'}
        </div>
      )}

      <style jsx>{`
        .admin-page {
          min-height: 60vh;
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }

        /* Sunday Dashboard */
        .sunday-dashboard {
          margin-bottom: 32px;
          width: 100%;
          max-width: 100%;
        }

        .dashboard-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .dashboard-header h2 {
          margin: 0;
        }

        .btn-copy-text {
          padding: 6px 14px;
          background: var(--accent-primary);
          border: 1px solid rgba(255, 255, 255, 0.25);
          border-radius: 6px;
          color: #fff;
          cursor: pointer;
          font-size: 0.875rem;
          font-weight: 500;
          flex-shrink: 0;
        }

        .btn-copy-text:hover {
          background: var(--accent-primary);
        }

        .btn-small {
          padding: 6px 12px;
          background: var(--accent-primary);
          border: none;
          border-radius: 4px;
          color: white;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .btn-small:hover {
          background: var(--accent-primary);
        }

        /* Church Cards */
        .church-cards {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 16px;
          width: 100%;
        }

        .church-card {
          display: flex;
          flex-direction: column;
          background: var(--bg-card);
          border-radius: var(--border-radius);
          border-left: 4px solid;
          padding: 16px;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          min-height: 100%;
        }

        .church-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .church-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
          flex-shrink: 0;
        }

        .church-name {
          font-weight: 600;
          font-size: 1rem;
        }

        .church-card-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        .btn-refresh-youtube {
          padding: 2px 6px;
          font-size: 0.85rem;
          line-height: 1.2;
          border-radius: 4px;
          border: 1px solid var(--border-color);
          background: transparent;
          color: var(--text-secondary);
          cursor: pointer;
        }

        .btn-refresh-youtube:hover:not(:disabled) {
          color: var(--accent-primary);
          border-color: var(--accent-primary);
        }

        .btn-refresh-youtube:disabled {
          opacity: 0.5;
          cursor: wait;
        }

        .btn-copy-church {
          flex-shrink: 0;
          padding: 2px 8px;
          font-size: 0.7rem;
          line-height: 1.3;
          border-radius: 4px;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
        }

        .btn-copy-church:disabled {
          opacity: 0.7;
          cursor: wait;
        }

        .btn-copy-church.btn-copy-ready {
          border: 1px solid #15803d;
          background: rgba(22, 163, 74, 0.14);
          color: #15803d;
        }

        .btn-copy-church.btn-copy-ready:hover:not(:disabled) {
          background: rgba(22, 163, 74, 0.22);
        }

        .btn-copy-church.btn-copy-pending {
          border: 1px solid #ca8a04;
          background: rgba(234, 179, 8, 0.16);
          color: #a16207;
        }

        .btn-copy-church.btn-copy-pending:hover:not(:disabled) {
          background: rgba(234, 179, 8, 0.26);
        }

        .church-yt-warning {
          margin: 0 0 8px;
          font-size: 0.72rem;
          line-height: 1.3;
          color: #a16207;
        }

        .church-card-body {
          flex: 1;
          display: grid;
          grid-template-rows: repeat(2, minmax(4.75rem, 1fr));
          align-content: stretch;
        }

        .entries-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .entry-item {
          padding: 4px 0;
        }

        .entry-type {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .entry-title {
          font-size: 0.9rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .entry-speaker {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .no-entry {
          color: var(--text-muted);
          font-size: 0.9rem;
          text-align: center;
          padding: 24px 0;
        }

        /* All Events Section */
        .all-events-section {
          margin-top: 32px;
        }

        .all-events-section h3 {
          margin-bottom: 16px;
        }

        .events-table {
          background: var(--bg-card);
          border-radius: var(--border-radius);
          overflow: hidden;
        }

        .events-table-header {
          display: grid;
          grid-template-columns: 120px 60px 60px 1fr 80px 100px 80px 60px;
          padding: 12px 16px;
          background: var(--bg-secondary);
          font-weight: 600;
          font-size: 0.875rem;
        }

        .events-table-body {
          max-height: 400px;
          overflow-y: auto;
        }

        .events-table-row {
          display: grid;
          grid-template-columns: 120px 60px 60px 1fr 80px 100px 80px 60px;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border-color);
          cursor: pointer;
          transition: background 0.2s;
        }

        .events-table-row:hover {
          background: var(--bg-hover);
        }

        .events-table-row.past-event {
          background: var(--bg-tertiary);
          color: var(--text-secondary);
        }

        .events-table-row.past-event:hover {
          background: var(--bg-hover);
        }

        .events-table-row.past-event .text-muted {
          color: var(--text-muted);
        }

        .events-table-row:last-child {
          border-bottom: none;
        }

        .col-date {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .day-of-week {
          font-size: 0.8rem;
          font-weight: 500;
        }

        .day-of-week.sunday {
          color: #dc2626;
        }

        .day-of-week.saturday {
          color: #16a34a;
        }

        .col-title {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .no-events {
          padding: 24px;
          text-align: center;
          color: var(--text-muted);
        }

        /* Badge styles */
        .badge {
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
        }

        .badge-success {
          background: var(--accent-success);
          color: white;
        }

        .badge-warning {
          background: var(--accent-warning);
          color: white;
        }

        .badge-danger {
          background: var(--accent-danger);
          color: white;
        }

        .badge-orange {
          background: #f97316;
          color: white;
        }

        .badge-orange-clickable {
          border: none;
          cursor: pointer;
          font: inherit;
          line-height: inherit;
        }

        .badge-orange-clickable:hover {
          background: #ea580c;
          box-shadow: 0 0 0 2px rgba(249, 115, 22, 0.35);
        }

        /* Entry type row — fixed slots so 主日學 / 講道 align across cards */
        .entry-row-inline {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 8px;
          padding: 8px 0;
          border-bottom: 1px solid var(--border-color);
          min-height: 4.75rem;
          box-sizing: border-box;
        }

        .entry-row-inline:last-child {
          border-bottom: none;
        }

        .entry-row-label {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
          flex: 1;
        }

        .entry-meta {
          display: block;
          font-size: 0.68rem;
          line-height: 1.3;
          min-height: 1.3em;
          color: var(--text-secondary, #94a3b8);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 100%;
        }

        .entry-meta.is-empty {
          visibility: hidden;
        }

        .entry-row-status {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
          padding-top: 2px;
          min-height: 1.5rem;
        }

        .file-indicator-wrap {
          position: relative;
        }

        .btn-copy-entry {
          font-size: 0.75rem;
          line-height: 1;
          padding: 2px 4px;
          background: none;
          border: none;
          cursor: pointer;
          color: var(--accent-primary);
          font-weight: 600;
        }

        .btn-copy-entry:hover {
          color: var(--accent-secondary);
        }

        .copy-success-msg {
          margin-top: 8px;
          font-size: 0.875rem;
          color: #22c55e;
        }

        .file-indicator {
          font-size: 0.8rem;
          color: #ea580c;
          line-height: 1;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
        }

        .file-indicator:hover {
          color: #c2410c;
        }

        .attachment-menu {
          position: absolute;
          right: 0;
          top: 100%;
          z-index: 20;
          margin-top: 4px;
          min-width: 140px;
          background: var(--card-bg, #1e293b);
          border: 1px solid var(--border-color);
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          padding: 4px 0;
        }

        .attachment-menu-item {
          display: block;
          width: 100%;
          text-align: left;
          padding: 6px 10px;
          font-size: 0.75rem;
          color: var(--text-primary);
          background: none;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 200px;
        }

        .attachment-menu-item:hover {
          background: rgba(255, 255, 255, 0.08);
          color: #ea580c;
        }

        .entry-row-inline:last-child {
          border-bottom: none;
        }

        .entry-type-label {
          font-size: 0.875rem;
          font-weight: 500;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border-radius: 12px;
          width: 90%;
          max-width: 700px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 24px;
          border-bottom: 1px solid var(--border-color);
          position: sticky;
          top: 0;
          background: var(--bg-card);
        }

        .btn-close {
          background: none;
          border: none;
          color: var(--text-secondary);
          font-size: 24px;
          cursor: pointer;
        }

        .modal-body {
          padding: 24px;
        }

        .copy-modal {
          max-width: 800px;
        }

        .modal-subtitle {
          margin-left: 8px;
          font-size: 0.9rem;
          font-weight: 400;
          color: var(--text-muted);
        }

        .copy-preview {
          width: 100%;
          min-height: 200px;
          max-height: 420px;
          overflow-y: auto;
          padding: 16px;
          background: #fff;
          color: #1a1a1a;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius);
        }

        .copy-preview a {
          color: #2563eb;
          text-decoration: underline;
        }

        .copy-hint {
          margin: 12px 0 0;
          font-size: 0.8rem;
          color: var(--text-muted);
        }

        .copy-loading {
          text-align: center;
          color: var(--text-muted);
          padding: 48px 0;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 24px;
          border-top: 1px solid var(--border-color);
        }

        .btn-secondary {
          padding: 8px 16px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 4px;
          color: var(--text-primary);
          cursor: pointer;
        }

        .btn-secondary:hover {
          background: var(--bg-hover);
        }

        .church-detail {
          margin-bottom: 24px;
        }

        .church-detail h4 {
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--border-color);
        }

        .entry-detail {
          background: var(--bg-secondary);
          padding: 12px;
          border-radius: var(--border-radius);
          margin-bottom: 8px;
        }

        .entry-row {
          display: flex;
          gap: 8px;
          margin-bottom: 4px;
        }

        .entry-row .label {
          color: var(--text-muted);
          min-width: 80px;
        }

        .entry-row a {
          color: var(--accent-primary);
          word-break: break-all;
        }

        .no-entry-detail {
          color: var(--text-muted);
          text-align: center;
          padding: 24px;
        }

        /* Toast */
        .toast {
          position: fixed;
          bottom: 24px;
          right: 24px;
          background: var(--accent-success);
          color: white;
          padding: 12px 24px;
          border-radius: var(--border-radius);
          z-index: 1100;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @media (max-width: 1024px) {
          .church-cards {
            grid-template-columns: repeat(2, 1fr);
          }

          .events-table-header,
          .events-table-row {
            grid-template-columns: 1fr;
            gap: 4px;
          }

          .col-date::before { content: 'Date: '; font-weight: 600; }
          .col-time::before { content: 'Time: '; font-weight: 600; }
          .col-church::before { content: 'Church: '; font-weight: 600; }
          .col-title::before { content: 'Title: '; font-weight: 600; }
          .col-type::before { content: 'Type: '; font-weight: 600; }
          .col-speaker::before { content: 'Speaker: '; font-weight: 600; }
          .col-status::before { content: 'Status: '; font-weight: 600; }
          .col-files::before { content: 'Files: '; font-weight: 600; }
        }

        @media (max-width: 1024px) {
          .church-cards {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 768px) {
          .church-cards {
            grid-template-columns: 1fr;
          }

          .dashboard-header {
            flex-direction: row;
            align-items: center;
          }
        }
      `}</style>
    </div>
  )
}